/**
 * Pure logic for the job-requisition approval flow (Jobs → ATS `POST
 * /jobs/:id/requisition/submit`, routed through User Management's engine).
 *
 * Kept dependency-free (no antd, no i18n, no redux) so it is trivial to unit test
 * and so the two things it decides — "what state is this job actually in" and
 * "is this failure the well-known workflow-not-configured case" — cannot drift
 * from what the two backends actually send.
 *
 * ── Why `job_status: 'pending'` is not enough on its own ─────────────────────
 * The ATS's `Jobs.job_status` column (see `hrms-ats-service/src/jobs/entities/job.entity.ts`)
 * reuses the SAME string, `'pending'`, for two unrelated things:
 *   1. A client-portal job awaiting a recruiter's own vetting decision
 *      (`JobsService.create`'s external-client path) — no approval instance exists.
 *   2. A requisition awaiting a decision from User Management's approval engine
 *      (`JobsService.submitRequisition`) — `ApprovalInstanceId` is set.
 * `ApprovalInstanceId` (camelCased `approvalInstanceId` on the wire, from
 * `findOne`'s `j.ApprovalInstanceId AS approvalInstanceId`) is the only field that
 * tells the two apart, so every consumer must check both fields together rather
 * than pattern-matching on `job_status` alone.
 */

export interface JobRequisitionStatusInput {
  job_status?: string | null;
  approvalInstanceId?: number | null;
}

export type JobRequisitionStateCode =
  | 'open'
  | 'closed'
  | 'approved'
  | 'pendingApproval'
  | 'pendingClientReview'
  | 'unknown';

/**
 * What state a job is actually in, distinguishing the two meanings of
 * `job_status === 'pending'` described above.
 */
export const resolveJobRequisitionState = (
  job: JobRequisitionStatusInput,
): JobRequisitionStateCode => {
  const status = (job.job_status ?? '').toString().trim().toLowerCase();

  if (status === 'pending') {
    return job.approvalInstanceId != null ? 'pendingApproval' : 'pendingClientReview';
  }
  if (status === 'open') return 'open';
  if (status === 'close' || status === 'closed') return 'closed';
  if (status === 'approved') return 'approved';
  return 'unknown';
};

/** Antd `Tag` colour per state — never a bare synonym of "open" for a pending job. */
export const JOB_REQUISITION_STATE_TAG_COLOR: Record<JobRequisitionStateCode, string> = {
  open: 'success',
  closed: 'error',
  approved: 'blue',
  pendingApproval: 'gold',
  pendingClientReview: 'orange',
  unknown: 'default',
};

/** i18n key (namespace `recruitment`) for each state's label. Render with `t()`. */
export const JOB_REQUISITION_STATE_LABEL_KEY: Record<JobRequisitionStateCode, string> = {
  open: 'recruitment:requisition.stateOpen',
  closed: 'recruitment:requisition.stateClosed',
  approved: 'recruitment:requisition.stateApproved',
  pendingApproval: 'recruitment:requisition.statePendingApproval',
  pendingClientReview: 'recruitment:requisition.statePendingClientReview',
  unknown: 'recruitment:requisition.stateUnknown',
};

/**
 * May this job be submitted as a requisition right now?
 *
 * Mirrors the two guards `JobsService.submitRequisition` throws `ConflictException`
 * for (already has an instance; already pending client vetting) — checked here too
 * so the "Submit for approval" button disables itself instead of round-tripping to
 * the server just to learn it will refuse.
 */
export const canSubmitJobRequisition = (job: JobRequisitionStatusInput): boolean => {
  const state = resolveJobRequisitionState(job);
  return state !== 'pendingApproval' && state !== 'pendingClientReview';
};

// ── Workflow-not-configured detection ────────────────────────────────────────

/**
 * Does this error message describe User Management's
 * `BusinessRuleException.WorkflowNotConfigured` (HRMS-174 / D6)?
 *
 * There is no shared error CODE across the hop: UM's `ApprovalController.StartWorkflow`
 * catches every exception and replies `400 { success: false, message: ex.Message }`
 * (see `Controllers/ApprovalController.cs`) — the `WORKFLOW_NOT_CONFIGURED` code on
 * the exception itself never reaches the wire. The ATS's `ApprovalEngineClient.start`
 * then wraps ANY failure from that call as `ServiceUnavailableException` (503), copying
 * only the `message` string through (`approval-engine.client.ts`). So by the time this
 * SPA sees it, the transport status is a generic 503 and the ONLY signal left is UM's
 * literal sentence:
 *
 *   "No approval workflow is configured for module 'JobRequisition' in this
 *   organization. An administrator must provision the approval workflow before
 *   this action can be submitted."
 *
 * Matched loosely (not as an exact string) so a small wording tweak on either
 * backend does not silently turn this back into an unhandled generic error.
 */
export const isWorkflowNotConfiguredMessage = (message: string | null | undefined): boolean => {
  if (!message) return false;
  const text = message.toLowerCase();
  if (!text.includes('workflow')) return false;
  return (
    text.includes('administrator must provision') ||
    text.includes('is configured for module') ||
    text.includes('not configured')
  );
};

export interface RequisitionSubmitFailure {
  message: string;
  workflowNotConfigured: boolean;
}

/** Build the classified failure shape the thunk rejects with, from a raw error message. */
export const classifyRequisitionSubmitError = (
  message: string | null | undefined,
): RequisitionSubmitFailure => ({
  message: message || 'Failed to submit requisition for approval',
  workflowNotConfigured: isWorkflowNotConfiguredMessage(message),
});
